import {RefObject, useEffect, useRef} from "react";

interface ScrollableModel {
    events: {
        on(event: string, handler: (callback?: () => void) => void): () => void;
    };
}

interface UseModelScrollOptions {
    eventName?: string;
    behavior?: ScrollBehavior;
    offset?: number;
    onScrollEnd?: () => void;
    scrollDuration?: number;
}

export function useModelScroll<TElement extends HTMLElement = HTMLElement>(
    model: ScrollableModel,
    options: UseModelScrollOptions = {},
): RefObject<TElement | null> {
    const {eventName = "scrollToView", behavior = "smooth", offset = 0, onScrollEnd, scrollDuration = 500} = options;

    const ref = useRef<TElement>(null);

    useEffect(() => {
        const unsubscribe = model.events.on(eventName, (callback) => {
            if (ref.current) {
                const elementPosition = ref.current.getBoundingClientRect().top;
                const offsetPosition = elementPosition + window.pageYOffset + offset;

                window.scrollTo({
                    top: offsetPosition,
                    behavior,
                });

                const finalCallback = callback || onScrollEnd;
                if (finalCallback) {
                    setTimeout(finalCallback, behavior === "smooth" ? scrollDuration : 0);
                }
            }
        });

        return () => {
            unsubscribe();
        };
    }, [model, eventName, behavior, offset, onScrollEnd, scrollDuration]);

    return ref;
}
